import { css } from '@emotion/css'

import { borderRadius, fontFamily, fontSize, spacing } from '../../../styles'
import { shorthandHelper } from '../../../styles/helpers'
import getThemeColors, { colors } from '../../../themes'
import { hexToRgba } from '../../../themes/helpers'
import { accessiblyHidden } from '../../../utils/styleUtils'

const fieldWidth = 240
const fieldHeight = 40
const fieldBorderWidth = 1
const fieldLineHeight = fieldHeight - fieldBorderWidth * 2
const selectWidth = 106
const caretWidth = 18
const flagWidth = 16

const styles = {
  fieldset: css({
    legend: {
      marginBottom: spacing.small,
    },
  }),

  wrapper: css({
    width: fieldWidth,
    maxWidth: '100%',
    height: fieldHeight,
    boxSizing: 'border-box',
    borderRadius: borderRadius.default,
    border: `${fieldBorderWidth}px solid ${colors.mystic}`,
    padding: 0,
    margin: 0,
    display: 'flex',
    position: 'relative',
    fontFamily: fontFamily.body,

    '::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      width: selectWidth,
      height: '100%',
      backgroundColor: colors.athensGray,
      borderRadius: shorthandHelper([borderRadius.default, 0, 0, borderRadius.default]),
    },
  }),

  errorWrapper: css({
    borderColor: colors.mandy,

    ':focus-within': {
      borderColor: colors.mystic,
    },

    'select:focus, input:focus': {
      borderColor: colors.mandy,
      boxShadow: `0 0 0 2px ${hexToRgba(colors.mandy, 0.25)}`,
    },
  }),

  selectLabel: css(accessiblyHidden),

  select: css({
    width: selectWidth,
    minWidth: selectWidth,
    padding: shorthandHelper([0, spacing.default]),
    border: `${fieldBorderWidth}px solid transparent`,
    borderRightColor: colors.mystic,
    borderRadius: shorthandHelper([borderRadius.default, 0, 0, borderRadius.default]),
    appearance: 'none',
    color: 'transparent',
    zIndex: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    outline: 'none',

    'option, optgroup': {
      color: colors.blackPearl,
    },
  }),

  currentFlag: css({
    position: 'absolute',
    left: spacing.default,
    top: 0,
    fontSize: fieldHeight - spacing.default * 2,
    lineHeight: `${fieldLineHeight}px`,
  }),

  caret: {
    color: colors.grayChateau,
    width: caretWidth,
    styles: css({
      position: 'absolute',
      left: spacing.default + flagWidth + spacing.extraSmall,
      top: 0,
      minHeight: '100%',
    }),
  },

  currentPrefix: css({
    position: 'absolute',
    left: spacing.default + flagWidth + spacing.extraSmall + caretWidth + spacing.extraSmall,
    top: 0,
    color: colors.jumbo,
    lineHeight: `${fieldLineHeight}px`,
    fontFamily: fontFamily.body,
    fontSize: fontSize.default,
  }),

  inputLabel: css(accessiblyHidden),

  input: css({
    width: '100%',
    boxSizing: 'border-box',
    border: `${fieldBorderWidth}px solid transparent`,
    borderRadius: shorthandHelper([0, borderRadius.default, borderRadius.default, 0]),
    padding: shorthandHelper([0, spacing.default]),

    color: colors.jumbo,
    fontSize: fontSize.default,
    fontFamily: fontFamily.body,

    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    outline: 'none',
    appearance: 'none',
  }),
}

export default styles

export const wrapperTheme = theme => {
  const themed = getThemeColors(theme)
  return css({
    'input:focus, select:focus': {
      zIndex: 0,
      borderColor: themed.base,
      boxShadow: `0 0 0 2px ${hexToRgba(themed.base, 0.25)}`,
    },
  })
}
